<template>
  <div class="schedule-container">

    <header class="page-header">
      <div class="title-row">
        <div class="top-controls">
          <select v-model="scheduleType" class="schedule-type-select">
            <option value="groups">Расписание учебных групп</option>
            <option value="teachers">Расписание преподавателей</option>
          </select>
        </div>

        <div class="title-group">
          <i class="icon-clock">🕒</i>
          <h1 v-if="scheduleStore.currentSemester">
            {{scheduleStore.currentSemester.name}}
          </h1>
        </div>

        <div class="search-box">
          <input
            type="text"
            :placeholder="searchPlaceholder"
            v-model="searchQuery"
          />
          <button class="search-btn">🔍</button>
        </div>
      </div>
    </header>

    <nav class="tabs-nav" v-if="scheduleType === 'groups'">
      <ul>
        <li
          v-for="tab in tabs"
          :key="tab.id"
          :class="{ active: selectedTabId === tab.id }"
          @click="selectTab(tab.id)"
        >
          {{ tab.name }}
        </li>
      </ul>
    </nav>

    <main class="content-area">

      <div v-if="scheduleType === 'groups'">
        <h2 class="section-title">{{ currentTabName }}</h2>

        <div class="columns-grid" style="margin-bottom: 100px;" v-if="currentItems.length">
          <div
            class="course-column"
            v-for="(course, index) in currentItems"
            :key="index"
          >
            <h3 class="course-header">{{ course.year }}</h3>
            <div class="group-list">
              <button
                class="group-item"
                v-for="group in course.list"
                :key="group.id"
                @click="handleItemClick(group.id, group.title)"
              >
                {{ group.title }}
              </button>
            </div>
          </div>
        </div>

        <div v-else class="empty-state">
          <span v-if="searchQuery">Группы с названием "{{ searchQuery }}" не найдены</span>
          <span v-else>Нет данных для отображения</span>
        </div>
      </div>

      <div v-else class="teacher-view">

        <div class="alphabet-wrapper">
          <div class="alphabet-container">
            <button
              v-for="letter in alphabet"
              :key="letter"
              class="letter-btn"
              :class="{ active: selectedLetter === letter }"
              @click="filterByLetter(letter)"
            >
              {{ letter }}
            </button>

            <button
              class="letter-btn clear-btn"
              v-if="selectedLetter || searchQuery"
              @click="resetTeacherView"
            >
              ✕
            </button>
          </div>
        </div>

        <div class="teacher-list-container">
          <div v-if="loadingTeachers" class="loading-state">
            Загрузка...
          </div>

          <div v-else-if="visibleTeachers.length > 0">
            <div class="teachers-grid" style="margin-bottom: 100px;">
              <div
                v-for="teacher in visibleTeachers"
                :key="teacher.id"
                class="teacher-row"
                @click="handleTutorClick(teacher.id , teacher.name)"
              >
                {{ teacher.name }}
              </div>
            </div>

            <div class="pagination-controls" v-if="!selectedLetter && !searchQuery">
              <button
                class="page-btn"
                :disabled="currentPage === 1"
                @click="changePage(-1)"
              >
                &laquo; Назад
              </button>

              <span class="page-info">Страница {{ currentPage }}</span>

              <button
                class="page-btn"
                :disabled="teachersList.length < pageSize"
                @click="changePage(1)"
              >
                Вперед &raquo;
              </button>
            </div>
          </div>

          <div v-else class="empty-state">
            <span v-if="searchQuery">Преподаватели с именем "{{ searchQuery }}" не найдены</span>
            <span v-else>Преподаватели не найдены</span>
          </div>
        </div>

      </div>

    </main>

<!--1-->

  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance, watch } from 'vue';
import { fetchSchedule, scheduleStore , fetchCurrentSemestr} from '@/store/schedule';
import { API_URL } from '@/server';

const instance = getCurrentInstance();
const router = (instance && instance.proxy) ? instance.proxy.$router : null;

// --- CONSTANTS ---
const tabs = [
  { id: "bachelor", name: "Бакалавриат" },
  // { id: "specialist", name: "Специалитет" },
  { id: "magistr", name: "Магистратура" },
  // { id: "phd", name: "Докторантура" }
];

const alphabet = [
  'А', 'Б', 'В', 'Г', 'Д', 'Е', 'Ё', 'Ж', 'З', 'И',
  'К', 'Л', 'М', 'Н', 'О', 'П', 'Р', 'С', 'Т', 'У',
  'Ф', 'Х', 'Ц', 'Ч', 'Ш', 'Щ', 'Э', 'Ю', 'Я'
];

// --- STATE ---
const selectedTabId = ref("bachelor");
const scheduleType = ref("groups");
const searchQuery = ref("");

// Teacher specific state
const teachersList = ref([]);
const selectedLetter = ref(null);
const loadingTeachers = ref(false);
const currentPage = ref(1);
const pageSize = ref(100);

// --- LIFECYCLE ---
onMounted(() => {
  fetchSchedule();
  fetchCurrentSemestr();
});

// Watch mode switching to reset state
watch(scheduleType, (newVal) => {
  searchQuery.value = "";
  if (newVal === 'teachers' && teachersList.value.length === 0) {
    loadAllTeachers(1);
  }
});

// --- COMPUTED PROPERTIES ---

const searchPlaceholder = computed(() => {
  return scheduleType.value === 'groups'
    ? 'Поиск группы (напр. 101)'
    : 'Поиск преподавателя...';
});

const currentTabName = computed(() => {
  const tab = tabs.find(t => t.id === selectedTabId.value);
  return tab ? tab.name : '';
});

// 1. FILTER GROUPS (Manual)
const currentItems = computed(() => {
  const allItems = scheduleStore.items || {};
  const rawList = allItems[selectedTabId.value] || [];

  if (!searchQuery.value || scheduleType.value !== 'groups') {
    return rawList;
  }

  const query = searchQuery.value.toLowerCase();
  return rawList.map(column => {
    return {
      ...column,
      list: column.list.filter(group =>
        group.title.toLowerCase().includes(query)
      )
    };
  }).filter(column => column.list.length > 0);
});

// 2. FILTER TEACHERS (Manual)
const visibleTeachers = computed(() => {
  if (!searchQuery.value || scheduleType.value !== 'teachers') {
    return teachersList.value;
  }
  const query = searchQuery.value.toLowerCase();
  return teachersList.value.filter(teacher =>
    teacher.name && teacher.name.toLowerCase().includes(query)
  );
});

// --- API METHODS ---

const loadAllTeachers = async (page = 1) => {
  loadingTeachers.value = true;
  selectedLetter.value = null;
  currentPage.value = page;

  try {
    const response = await fetch(`${API_URL}/api/tutor?PageSize=${pageSize.value}&PageIndex=${page}`);
    const res = await response.json();
    if(res.code === 200) {
      teachersList.value = res.data;
    }
  } catch (error) {
    console.error("Error fetching tutors:", error);
  } finally {
    loadingTeachers.value = false;
  }
};

const filterByLetter = async (letter) => {
  selectedLetter.value = letter;
  searchQuery.value = "";
  loadingTeachers.value = true;

  try {
    const response = await fetch(`${API_URL}/api/tutor/get-by-char?letter=${letter}`);
    const res = await response.json();
    if(res.code === 200) {
      teachersList.value = res.data;
    }
  } catch (error) {
    console.error("Error fetching tutors by char:", error);
  } finally {
    loadingTeachers.value = false;
  }
};

const resetTeacherView = () => {
  searchQuery.value = "";
  loadAllTeachers(1);
};

const changePage = (step) => {
  const newPage = currentPage.value + step;
  if (newPage > 0) {
    loadAllTeachers(newPage);
  }
};

// --- NAVIGATION ---
const selectTab = (id) => {
  selectedTabId.value = id;
};

const handleItemClick = (id, groupTitle) => {
  if (router) {
    router.push({
      path: `/study-groups/${id}/day`,
      query: { groupName: groupTitle }
    });
  }
};


const handleTutorClick = (id , tutorName) => {
  if (router) {
    router.push({
      path: `/tutor-schedule/${id}`,
      query: { tutorTitle: tutorName }
    });
  }
};
</script>

<style scoped>
/* GENERAL STYLES */
.schedule-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  color: #333;
}

/* HEADER LAYOUT */
.page-header {
  margin-bottom: 30px;
}

.title-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
}

.top-controls {
  flex-basis: 100%;
  display: flex;
  justify-content: flex-end;
}

.title-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-group h1 {
  font-weight: 300;
  font-size: 28px;
  color: #333;
  margin: 0;
}

.icon-clock {
  font-style: normal;
  font-size: 24px;
}

/* INPUT STYLES */
.schedule-type-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  font-size: 14px;
  outline: none;
  background-color: white;
  cursor: pointer;
  border-radius: 4px;
  height: 35px;
  min-width: 240px;
}

.search-box {
  display: flex;
}

.search-box input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-right: none;
  font-size: 14px;
  width: 200px;
  outline: none;
  height: 35px;
  box-sizing: border-box;
}

.search-box .search-btn {
  background-color: #0e7490;
  color: white;
  border: none;
  padding: 0 15px;
  cursor: pointer;
  height: 35px;
}

/* TABS NAV */
.tabs-nav {
  margin-bottom: 30px;
  margin-top: 20px;
}

.tabs-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  border-bottom: 2px solid #0e7490;
}

.tabs-nav li {
  padding: 12px 20px;
  cursor: pointer;
  margin-bottom: -2px;
  border-bottom: 2px solid transparent;
  font-size: 15px;
  color: #555;
  transition: all 0.2s;
}

.tabs-nav li:hover {
  color: #0e7490;
}

.tabs-nav li.active {
  color: #0e7490;
  font-weight: bold;
  border-bottom: 2px solid #0e7490;
  background-color: transparent;
}

/* MAIN CONTENT */
.section-title {
  font-weight: 300;
  font-size: 32px;
  margin-bottom: 25px;
  color: #333;
}

/* === TEACHER VIEW STYLES === */
.teacher-view {
  margin-top: 20px;
}

.alphabet-container {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.letter-btn {
  background: white;
  border: 1px solid #ddd;
  color: #555;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  padding: 0;
  flex-shrink: 0; /* Prevents shrinking on mobile flex */
}

.letter-btn:hover {
  border-color: #0e7490;
  color: #0e7490;
}

.letter-btn.active {
  background-color: #0e7490;
  color: white;
  border-color: #0e7490;
}

.clear-btn {
  color: #e63946;
  border-color: #e63946;
  margin-left: 10px;
  font-weight: bold;
}
.clear-btn:hover {
  background-color: #e63946;
  color: white;
}

.teacher-list-container {
  background: white;
  border: 1px solid #eee;
  border-radius: 4px;
}

.teacher-row {
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  font-size: 16px;
  color: #444;
  cursor: pointer;
  transition: background-color 0.1s ease;
}

.teacher-row:hover {
  background-color: #f0f9ff;
  color: #0e7490;
}

.teacher-row:last-child {
  border-bottom: none;
}

/* PAGINATION STYLES */
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  gap: 20px;
  background-color: #f8f9fa;
  border-top: 1px solid #eee;
}

.page-btn {
  padding: 8px 16px;
  background: white;
  border: 1px solid #ddd;
  cursor: pointer;
  color: #333;
  border-radius: 4px;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background-color: #0e7490;
  color: white;
  border-color: #0e7490;
}

.page-btn:disabled {
  background-color: #eee;
  color: #aaa;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.loading-state, .empty-state {
  padding: 40px;
  text-align: center;
  color: #888;
}

/* GROUP VIEW GRID */
.columns-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: flex-start;
}

.course-column {
  flex: 1;
  min-width: 160px;
  max-width: 220px;
}

.course-header {
  text-align: center;
  font-size: 18px;
  font-weight: 400;
  margin-bottom: 15px;
  color: #333;
}

.group-list {
  display: flex;
  flex-direction: column;
  gap: -1px;
}

.group-item {
  background: white;
  border: 1px solid #dee2e6;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  font-size: 14px;
  color: #495057;
  transition: background 0.1s, color 0.1s;
  margin-top: -1px;
}

.group-item:first-child {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  margin-top: 0;
}

.group-item:last-child {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

.group-item:hover {
  background-color: #f8f9fa;
  color: #0e7490;
  z-index: 1;
  border-color: #b3d7ff;
}

/* FOOTER STYLES */
.page-footer {
  position: fixed;
  right: 0;
  bottom: 0;
  width: 250px;
  margin-left: auto;
  margin-right: 1px;
  padding: 8px 12px;
  z-index: 100;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.footer-content {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.support-info {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  font-size: 14px;
  color: #444;
}

.support-icon {
  display: flex;
  text-decoration: none;
  transition: opacity 0.2s;
  font-size: 20px;
}

.support-icon.email { color: #0e7490; }
.support-icon.telegram { color: #0088cc; }

.support-icon:hover { opacity: 0.7; }

.developer-info {
  font-size: 12px;
  color: #888;
  font-weight: 400;
}
.icon-mail, .icon-tg { font-style: normal; }


/* ========================================= */
/* MOBILE ADAPTATION RULES          */
/* ========================================= */

@media (max-width: 768px) {
  /* CONTAINER & PADDING */
  .schedule-container {
    padding: 15px;
    padding-bottom: 60px; /* Space for footer if stuck, or scrolling room */
  }

  /* HEADER ADAPTATION */
  .title-row {
    flex-direction: column;
    align-items: stretch; /* Stretch children to full width */
    gap: 15px;
  }

  .top-controls {
    justify-content: center;
  }

  /* Full width inputs for easier touching */
  .schedule-type-select {
    width: 100%;
    min-width: 0; /* Reset desktop constraints */
  }

  .title-group {
    justify-content: center;
    text-align: center;
  }

  .title-group h1 {
    font-size: 24px;
  }

  /* Search box full width */
  .search-box {
    width: 100%;
    display: flex;
  }

  .search-box input {
    flex-grow: 1; /* Input takes all available space */
    width: auto;
  }

  /* GRID ADAPTATION */
  .columns-grid {
    display: flex;
    flex-direction: column; /* Stack columns vertically on mobile */
  }

  .course-column {
    max-width: 100%; /* Remove desktop width restriction */
    margin-bottom: 20px;
  }

  /* TEACHER VIEW ADAPTATION */

  /* Make alphabet horizontally scrollable instead of wrapping 5 rows high */
  .alphabet-container {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 10px;
    justify-content: flex-start;

    /* Hide scrollbar for cleaner look */
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }

  .alphabet-container::-webkit-scrollbar {
    display: none;
  }

  .letter-btn {
    flex: 0 0 40px; /* Fixed width for letters so they don't squish */
    height: 40px; /* Larger touch target */
    font-size: 16px;
  }

  .teachers-grid .teacher-row {
    padding: 18px 15px; /* Bigger touch targets */
  }

  /* FOOTER ADAPTATION */
  .page-footer {
    position: static; /* Unfix it so it doesn't float over content */
    width: 100%;
    margin: 30px 0 0 0;
    box-shadow: none;
    background: transparent;
    text-align: center;
    padding: 20px 0;
    border-top: 1px solid #eee;
    border-radius: 0;
  }

  .support-info {
    justify-content: center; /* Center align support icons */
  }

  .footer-content {
    align-items: center;
  }
}

@media (max-width: 480px) {
  /* Smaller adjustments for very narrow phones */
  .section-title {
    font-size: 24px;
    text-align: center;
  }

  .tabs-nav ul {
    justify-content: center;
  }

  .tabs-nav li {
    flex: 1;
    text-align: center;
    padding: 10px;
  }
}
</style>
